/* ==========================================================================
   WESLYNE — section behaviours
   Standalone, dependency-free, loaded with `defer`. Nothing here touches
   Horizon's own components, so cart / variant / search behaviour is untouched.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- reveal */
  var observer = null;

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    return observer;
  }

  function initReveal(root) {
    var nodes = root.querySelectorAll('.wl-reveal:not(.is-in)');
    if (!nodes.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = getObserver();
    nodes.forEach(function (node, i) {
      // Stagger only within a group, and cap it so nothing ever feels slow.
      if (!node.style.getPropertyValue('--wl-delay')) {
        node.style.setProperty('--wl-delay', Math.min(i % 6, 5) * 70 + 'ms');
      }
      io.observe(node);
    });
  }

  /* ------------------------------------------------------- counting figures */
  var countObserver = null;

  function animateCount(el) {
    var raw = el.getAttribute('data-wl-count') || el.textContent;
    var match = String(raw).match(/^(\D*)(\d[\d.,]*)(.*)$/);
    if (!match) return;

    var prefix = match[1];
    var suffix = match[3];
    var clean = match[2].replace(/,/g, '');
    var target = parseFloat(clean);
    if (isNaN(target)) return;

    var decimals = (clean.split('.')[1] || '').length;

    // A zero needs no journey, and neither does a reduced-motion visitor.
    if (reduceMotion.matches || target === 0) {
      el.textContent = prefix + target.toFixed(decimals) + suffix;
      return;
    }

    var duration = 1100;
    var start = null;

    function frame(now) {
      if (start === null) start = now;
      var progress = Math.min((now - start) / duration, 1);
      // easeOutCubic — fast at first, settling into the final number.
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (progress < 1) window.requestAnimationFrame(frame);
    }

    el.textContent = prefix + (0).toFixed(decimals) + suffix;
    window.requestAnimationFrame(frame);
  }

  function initCounters(root) {
    var nodes = root.querySelectorAll('[data-wl-count]:not([data-wl-counted])');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.setAttribute('data-wl-counted', ''); animateCount(n); });
      return;
    }

    if (!countObserver) {
      countObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            countObserver.unobserve(entry.target);
            animateCount(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.4 }
      );
    }

    nodes.forEach(function (n) {
      n.setAttribute('data-wl-counted', '');
      countObserver.observe(n);
    });
  }

  /* -------------------------------------------------------------- carousel */
  function initCarousels(root) {
    root.querySelectorAll('[data-wl-carousel]:not([data-wl-ready])').forEach(function (el) {
      el.setAttribute('data-wl-ready', '');

      var viewport = el.querySelector('[data-wl-viewport]');
      var prev = el.querySelector('[data-wl-prev]');
      var next = el.querySelector('[data-wl-next]');
      if (!viewport) return;

      viewport.setAttribute('tabindex', '0');
      viewport.setAttribute('role', 'region');

      function step() {
        var first = viewport.firstElementChild;
        if (!first) return viewport.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(viewport).columnGap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function scrollBy(dir) {
        viewport.scrollBy({
          left: dir * step(),
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
      }

      function sync() {
        var max = viewport.scrollWidth - viewport.clientWidth - 2;
        var atStart = viewport.scrollLeft <= 2;
        var atEnd = viewport.scrollLeft >= max;
        var single = max <= 2;
        if (prev) prev.disabled = atStart || single;
        if (next) next.disabled = atEnd || single;
        el.toggleAttribute('data-wl-static', single);
      }

      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });

      viewport.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); scrollBy(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); scrollBy(-1); }
      });

      viewport.addEventListener('scroll', function () {
        window.requestAnimationFrame(sync);
      }, { passive: true });

      window.addEventListener('resize', function () {
        window.requestAnimationFrame(sync);
      }, { passive: true });

      sync();
    });
  }

  /* ------------------------------------------------------------- accordion */
  function initAccordions(root) {
    root.querySelectorAll('[data-wl-acc]:not([data-wl-ready])').forEach(function (acc) {
      acc.setAttribute('data-wl-ready', '');
      var single = acc.hasAttribute('data-wl-acc-single');
      var buttons = acc.querySelectorAll('[data-wl-acc-btn]');

      buttons.forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;

        btn.addEventListener('click', function () {
          var open = btn.getAttribute('aria-expanded') === 'true';

          if (!open && single) {
            buttons.forEach(function (other) {
              if (other === btn) return;
              var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
              other.setAttribute('aria-expanded', 'false');
              if (otherPanel) otherPanel.setAttribute('data-open', 'false');
            });
          }

          btn.setAttribute('aria-expanded', String(!open));
          panel.setAttribute('data-open', String(!open));
        });
      });
    });
  }

  /* ------------------------------------------------------------------ tabs */
  function initTabs(root) {
    root.querySelectorAll('[data-wl-tabs]:not([data-wl-ready])').forEach(function (el) {
      el.setAttribute('data-wl-ready', '');
      var btns = Array.prototype.slice.call(el.querySelectorAll('[data-wl-tab-btn]'));
      if (!btns.length) return;

      function select(btn, focus) {
        btns.forEach(function (b) {
          var panel = document.getElementById(b.getAttribute('aria-controls'));
          var on = b === btn;
          b.setAttribute('aria-selected', String(on));
          b.setAttribute('tabindex', on ? '0' : '-1');
          if (panel) panel.hidden = !on;
        });
        if (focus) btn.focus();
        if (btn.scrollIntoView) {
          btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }

      btns.forEach(function (btn, i) {
        btn.addEventListener('click', function () { select(btn, false); });
        btn.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = btns[(i + 1) % btns.length];
          if (e.key === 'ArrowLeft') next = btns[(i - 1 + btns.length) % btns.length];
          if (e.key === 'Home') next = btns[0];
          if (e.key === 'End') next = btns[btns.length - 1];
          if (!next) return;
          e.preventDefault();
          select(next, true);
        });
      });

      var current = el.querySelector('[data-wl-tab-btn][aria-selected="true"]') || btns[0];
      select(current, false);
    });
  }

  /* ------------------------------------------------------ progressive reveal */
  function initShowMore(root) {
    root.querySelectorAll('[data-wl-more]:not([data-wl-ready])').forEach(function (el) {
      el.setAttribute('data-wl-ready', '');
      var btn = el.querySelector('[data-wl-more-btn]');
      var items = el.querySelectorAll('[data-wl-more-item]');
      if (!btn || !items.length) return;

      btn.addEventListener('click', function () {
        items.forEach(function (item) { item.hidden = false; });
        initReveal(el);
        if (btn.parentNode) btn.parentNode.removeChild(btn);
      });
    });
  }

  /* ------------------------------------------------------------------ boot */
  function init(root) {
    var scope = root || document;
    initReveal(scope);
    initCounters(scope);
    initCarousels(scope);
    initAccordions(scope);
    initTabs(scope);
    initShowMore(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) { init(e.target); });

  window.WeslyneUI = { init: init };
})();
